import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { reservationsApi, resourcesApi, usersApi } from '../services/api';
import { CalendarEvent, Resource, User } from '../types';
import { toNumberId } from '../utils/typeUtils';

// Function to generate a consistent color based on resource ID
const getColorForResource = (resourceId: number): string => {
  // Array of colors for different resources
  const colors = [
    '#3788d8', // blue
    '#28a745', // green
    '#dc3545', // red
    '#fd7e14', // orange
    '#6f42c1', // purple
    '#20c997', // teal
    '#17a2b8', // cyan
    '#6c757d', // gray
  ];

  // Use modulo to ensure we don't go out of bounds
  return colors[resourceId % colors.length];
};

const Reservations: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const userId = params.id;

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<User | null>(null);

  const [resources, setResources] = useState<any[]>([]);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setLoading(true);
        // Get date range for the next 3 months
        const today = new Date();

        // Format date as ISO string (YYYY-MM-DDTHH:MM:SSZ)
        const startDate = today.toISOString();
        const endDate = new Date(today);
        endDate.setMonth(today.getMonth() + 3);

        console.log(`Fetching reservations from ${startDate} to ${endDate.toISOString()}`);
        console.log('User ID from params:', userId);

        // Fetch reservations based on whether a userId is provided
        let reservations, users, resourcesList;

        if (userId) {
          // Convert userId to number for API calls
          const userIdNum = toNumberId(userId);
          if (!userIdNum) {
            console.error('Invalid user ID format:', userId);
            setError('Invalid user ID format');
            return;
          }

          // Fetch user-specific reservations
          const results = await Promise.all([
            reservationsApi.getByUser(userIdNum, startDate, endDate.toISOString()),
            usersApi.getAll(),
            resourcesApi.getAll(),
            usersApi.getById(userIdNum)
          ]);

          reservations = results[0];
          users = results[1];
          resourcesList = results[2];
          const userDetails = results[3];

          if (userDetails) {
            setUserInfo(userDetails);
          }
        } else {
          // Fetch all reservations
          [reservations, users, resourcesList] = await Promise.all([
            reservationsApi.getAll(startDate, endDate.toISOString()),
            usersApi.getAll(),
            resourcesApi.getAll()
          ]);
        }

        console.log('Reservations received:', reservations);
        console.log('Resources received:', resourcesList);

        // Create lookup maps for users and resources
        const userMap = new Map<number, User>();
        users.forEach(user => {
          // User ID is already a number in our updated types
          userMap.set(user.id, user);
        });

        const resourceMap = new Map<number, Resource>();
        resourcesList.forEach(resource => resourceMap.set(resource.id, resource));

        console.log('User map created:', Array.from(userMap.entries()));

        // Format resources for the timeline view
        const formattedResources = resourcesList.map(resource => ({
          id: resource.id.toString(),
          title: resource.name,
          category: resource.category,
          isActive: resource.isActive
        }));

        setResources(formattedResources);

        // Map the backend reservation format to the calendar event format
        const calendarEvents: CalendarEvent[] = reservations.map(reservation => {
          // Convert userId from string to number for map lookup
          const userIdNumber = typeof reservation.userId === 'string'
            ? parseInt(reservation.userId, 10)
            : reservation.userId;

          const user = userMap.get(userIdNumber);
          const resource = resourceMap.get(reservation.resourceId);

          console.log(`Mapping reservation: userId=${reservation.userId} (${typeof reservation.userId}) -> ${userIdNumber} (${typeof userIdNumber}), found user:`, user);

          return {
            id: reservation.id,
            title: `${resource?.name || `Resource ${reservation.resourceId}`}`,
            start: reservation.startDate,
            end: reservation.endDate,
            resourceId: reservation.resourceId.toString(), // Convert to string for FullCalendar resource ID
            userName: user?.name || `User ${reservation.userId}`,
            color: getColorForResource(reservation.resourceId),
            extendedProps: {
              notes: reservation.notes,
              resourceName: resource?.name || `Resource ${reservation.resourceId}`,
              userFullName: user?.name || `User ${reservation.userId}`
            }
          };
        });

        console.log('Calendar events:', calendarEvents);
        setEvents(calendarEvents);
      } catch (err) {
        console.error('Error fetching reservations:', err);
        setError('Failed to load reservations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, []);

  const handleDateClick = (arg: any) => {
    console.log('Date clicked:', arg.date);

    // Create a date object that preserves the exact date that was clicked
    // by setting the time to noon (to avoid timezone issues)
    const clickedDate = new Date(arg.date);
    clickedDate.setHours(12, 0, 0, 0);

    // Format the date to ensure correct date is passed (ISO format without time)
    // Use UTC methods to avoid timezone issues
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clickedDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    console.log('Clicked date object:', clickedDate);
    console.log('Formatted date for URL:', formattedDate);

    // Navigate to create reservation form with the selected date
    navigate(`/reservations/new?date=${formattedDate}`);
  };

  const handleEventClick = (arg: any) => {
    console.log('Event clicked:', arg.event.id);
    // Navigate to reservation details page using React Router
    navigate(`/reservations/${arg.event.id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {userInfo ? `${userInfo.name}'s Reservations` : 'Reservations'}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/reservations/new"
        >
          Create Reservation
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 2 }}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimelinePlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'resourceTimelineDay,resourceTimelineWeek,dayGridMonth'
          }}
          schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
          resources={resources}
          resourceAreaWidth="20%"
          slotDuration="01:00:00"
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }}
          events={events.map(event => {
            // Ensure resourceId is always a string for FullCalendar
            const resourceId = typeof event.resourceId === 'number'
              ? event.resourceId.toString()
              : event.resourceId;

            return {
              id: event.id,
              title: event.title,
              start: event.start,
              end: event.end,
              resourceId: resourceId,
              extendedProps: {
                userName: event.userName,
                userFullName: event.extendedProps?.userFullName,
                notes: event.extendedProps?.notes
              },
              backgroundColor: event.color || '#3788d8'
            };
          })}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          height="auto"
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            meridiem: 'short'
          }}
          eventContent={(arg) => {
            // Format the dates for display
            const startDate = new Date(arg.event.start!);
            const endDate = new Date(arg.event.end!);

            const formatTime = (date: Date) => {
              return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
                timeZone: 'America/Denver' // Mountain Time
              });
            };

            const dateStr = `${formatTime(startDate)} - ${formatTime(endDate)}`;

            return (
              <div>
                <b>{arg.event.title}</b>
                <div style={{ fontSize: '0.8em' }}>{dateStr}</div>
                <div style={{ fontSize: '0.8em' }}>{arg.event.extendedProps.userFullName}</div>
                {arg.event.extendedProps.notes && arg.event.extendedProps.notes !== 'New Reservation' && (
                  <div style={{ fontSize: '0.8em' }}>{arg.event.extendedProps.notes.substring(0, 30)}{arg.event.extendedProps.notes.length > 30 ? '...' : ''}</div>
                )}
              </div>
            );
          }}
        />
      </Paper>
    </Box>
  );
};

export default Reservations;
